import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Flower2, BookOpen, Coffee, Home, Brain, Sparkles, Compass, Baby, Target, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { BOOKING_URL } from '@/constants/urls';
import treatmentImage from '@/assets/treatment.jpg';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';

const PerimenopausePage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Chinese Medicine for Perimenopause Sunshine Coast | Hormonal Transition Support";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Supporting your hormonal transitions with clarity, care, and calm. Chinese medicine care for perimenopause symptoms, hot flushes, and hormonal balance in Currimundi.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Chinese Medicine for Perimenopause Sunshine Coast | Hormonal Transition Support");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Supporting your hormonal transitions with clarity, care, and calm. Chinese medicine care for perimenopause symptoms, hot flushes, and hormonal balance in Currimundi.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/perimenopause");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/treatment.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Chinese Medicine for Perimenopause Sunshine Coast | Hormonal Transition Support");
    }

    // Add LocalBusiness Schema for perimenopause services
    const perimenopauseSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Chinese Medicine for Perimenopause",
      "image": "https://womenandtheway.com.au/treatment.jpg",
      "description": "Supporting your hormonal transitions with clarity, care, and calm. Chinese medicine care for perimenopause symptoms, hot flushes, and hormonal balance in Currimundi.",
      "url": "https://womenandtheway.com.au/perimenopause",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Perimenopause Care, Hormonal Balance, Acupuncture, Chinese Medicine",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(perimenopauseSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                  Chinese Medicine for Perimenopause
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Supporting your hormonal transitions with clarity, care, and calm.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Perimenopause marks a powerful rite of passage in a woman's life. It can also arrive with unexpected intensity - heavy or irregular periods, sleep issues, anxiety, hot flushes, mood swings, and a sense that things are shifting beneath the surface. At Women and the Way, we honour this time of change and offer grounded support through acupuncture, Chinese herbs, and lifestyle wisdom.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  You don't have to wait until "it's all over" to feel like yourself again.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={treatmentImage} 
                  alt="Woman receiving gentle acupuncture for perimenopause support and hormonal balance" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Perimenopause Support Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center gap-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Moon className="w-8 h-8 text-sage-600" />
                Why Perimenopause Support Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Chinese medicine, perimenopause is a time when the balance of Yin and Yang begins to shift. The decline of Kidney Yin - your deep feminine essence - can lead to heat, dryness, emotional intensity, and irregular bleeding. When left unsupported, these changes may feel overwhelming or depleting.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                But with the right care, this can also be a time of awakening - a return to yourself, renewed clarity, and deep empowerment.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Our treatments are personalised to meet your unique symptoms, phase of transition, and life circumstances.
              </p>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Support for Perimenopause In-Clinic or Online
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Step into our calm Currimundi clinic for treatments designed to ease symptoms and reconnect you with your body's natural rhythms.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture to regulate hormones, calm heat, improve sleep, and soothe the nervous system</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal medicine to balance Yin and Yang, manage bleeding, and nourish depleted systems</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌙</span>
                      <p className="text-sage-700">Cycle awareness and transition planning - even if periods are changing, your rhythm matters</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💆‍♀️</span>
                      <p className="text-sage-700">Supportive bodywork to ease tension, improve circulation, and ground emotional shifts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    Support wherever you are in your journey.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Telehealth consults to talk through your symptoms, patterns, and priorities</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Custom herbal prescriptions mailed to your door or collected from the clinic</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📝</span>
                      <p className="text-sage-700">Personalised lifestyle and dietary strategies to support hormonal harmony and reduce overwhelm</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* The Perimenopausal Timeline */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                The Perimenopausal Timeline
              </h2>
              <p className="text-lg text-sage-600 mb-8">
                Because every journey is different.
              </p>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Early Perimenopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Subtle changes to mood, cycle length, or sleep</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Mid Perimenopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Heavier or skipped periods, hot flushes, anxiety, fatigue</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Moon className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Late Perimenopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Longer cycles, drier tissues, emotional processing, sleep issues</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Postmenopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Transition into a new baseline - support still matters here</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Common Symptoms We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Common Symptoms We Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Hot flushes & night sweats", chinese: "Yin deficiency, Empty Heat" },
                { western: "Mood swings, irritability, low mood", chinese: "Liver Qi stagnation, Heart/Spleen disharmony" },
                { western: "Fatigue & brain fog", chinese: "Qi and Blood deficiency, Kidney essence decline" },
                { western: "Insomnia", chinese: "Heart Yin deficiency, Liver Fire" },
                { western: "Heavy, long, or erratic periods", chinese: "Qi deficiency, Blood stasis, Yin deficiency" },
                { western: "Anxiety or emotional overwhelm", chinese: "Heart-Kidney imbalance, Shen disturbance" },
                { western: "Vaginal dryness or low libido", chinese: "Kidney Yin deficiency, Jing decline" },
                { western: "Digestive changes", chinese: "Spleen Qi deficiency, Dampness" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Our Care Different */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Care Different
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "We see the whole you", desc: "This isn't just about managing symptoms - it's about how you feel in your body, your life, and your identity" },
                { icon: "🌿", title: "Rooted in ancient wisdom", desc: "TCM offers a compassionate lens for understanding menopause as a transformation - not a dysfunction" },
                { icon: "📚", title: "Practical, personalised advice", desc: "Simple routines, herbal support, and emotional care that fits your real life" },
                { icon: "🌀", title: "A safe space for change", desc: "Whatever this transition is stirring up - we hold it with you" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                In-Clinic Tools for Hormonal Harmony
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Leaf, title: "Custom herbal formulas", desc: "In tea, capsule, or tincture form" },
                { icon: Coffee, title: "Warming teas", desc: "And Yin-nourishing broths" },
                { icon: Heart, title: "Castor oil packs", desc: "And heat therapy" },
                { icon: BookOpen, title: "Seed cycling guides", desc: "For irregular cycles" },
                { icon: BookOpen, title: "Recommended reading", desc: "The Wisdom of Menopause, Wild Power, Hormone Repair Manual" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Perimenopause Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              You deserve support that's as wise and powerful as the transition you're walking through. Let's help you feel calm, clear, and connected again.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : '🌱 Start where you are. We\'ll walk with you from there.'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>

        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default PerimenopausePage; 